import { PauseCircle } from "lucide-react";
import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import type { AiServicesOverview, AiServiceTotals, SvcService } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { formatAmount, SERVICE_LABEL, SVC_SERVICES, unitOf } from "@/lib/services-gateway";
import { cn } from "@/lib/utils";

import type { KillSwitchTarget } from "./kill-switch-dialog";

interface Stat {
  label: string;
  value: number | null;
  /** Worth a look when above 0. */
  alert?: boolean;
  hint?: string;
  href?: string;
}

/**
 * The headline numbers: the registry's month-to-date counts and what only
 * Scaffold UI knows (projects that bypass the gateway, Foundry roles overdue
 * for removal, budget requests waiting).
 */
export function OverviewStats({ overview }: { overview: AiServicesOverview }) {
  const registry = overview.registry ?? null;
  const stats: Stat[] = [
    { label: "Gateway projects", value: overview.gateway_projects },
    { label: "At 80 % of a budget", value: registry?.projects_at_80_percent ?? null },
    { label: "Over budget", value: registry?.projects_over_budget ?? null, alert: true },
    { label: "Suspended", value: registry?.projects_suspended ?? null, alert: true },
    {
      label: "Models direct",
      value: overview.direct_mode_projects,
      hint: "bypass the gateway: no model budget",
    },
    {
      label: "Foundry roles overdue",
      value: overview.overdue_role_removals,
      alert: true,
      hint: "still held past the grace period",
    },
    { label: "With a warning", value: overview.projects_with_warning, alert: true },
    {
      label: "Budget requests",
      value: overview.open_budget_requests,
      hint: "waiting for an admin",
      href: "/admin/service-budget-requests",
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
      {stats.map((stat) => (
        <Card key={stat.label}>
          <CardContent className="py-4">
            <p className="text-muted-foreground text-xs">{stat.label}</p>
            <p
              className={cn(
                "text-2xl font-bold tabular-nums",
                stat.alert && (stat.value ?? 0) > 0 ? "text-destructive" : "text-primary",
              )}
            >
              {stat.value ?? "—"}
            </p>
            {stat.href ? (
              <Link
                to={stat.href}
                className="text-primary text-xs underline-offset-4 hover:underline"
              >
                Open the queue
              </Link>
            ) : (
              stat.hint && <p className="text-muted-foreground text-xs">{stat.hint}</p>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/**
 * Per service: this month's use across every project, how many projects are
 * near or over budget or suspended, the models' spend per deployment, and
 * the service's kill switch. Without the registry's totals, the kill switches
 * alone -- their state unknown, so both ways -- since stopping or resuming
 * still works when the registry cannot count.
 */
export function ServiceCards({
  overview,
  onKillSwitch,
}: {
  overview: AiServicesOverview;
  onKillSwitch: (target: KillSwitchTarget) => void;
}) {
  const offGateway = (service: SvcService) =>
    service === "llm"
      ? overview.direct_mode_projects
      : service === "langfuse"
        ? (overview.dedicated_langfuse_projects ?? 0)
        : 0;
  const registry = overview.registry;
  if (!registry) {
    return <KillSwitchesOnly offGateway={offGateway} onKillSwitch={onKillSwitch} />;
  }
  const totals = registry.services;
  const ordered = SVC_SERVICES.map((service) => totals.find((t) => t.service === service)).filter(
    (t): t is AiServiceTotals => t !== undefined,
  );

  return (
    <div className="grid gap-4 md:grid-cols-3">
      {ordered.map((total) => (
        <ServiceCard
          key={total.service}
          total={total}
          deployments={total.service === "llm" ? registry.llm_deployments : []}
          offGatewayProjects={offGateway(total.service)}
          onKillSwitch={onKillSwitch}
        />
      ))}
    </div>
  );
}

function KillSwitchesOnly({
  offGateway,
  onKillSwitch,
}: {
  offGateway: (service: SvcService) => number;
  onKillSwitch: (target: KillSwitchTarget) => void;
}) {
  return (
    <Card>
      <CardContent className="space-y-3 py-4">
        <p className="text-muted-foreground text-xs">
          Kill switches: the registry did not say whether one is on. Stopping or running a service
          again for every project still works.
        </p>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          {SVC_SERVICES.map((service) => (
            <div key={service} className="flex items-center gap-2">
              <span className="text-sm">{SERVICE_LABEL[service]}</span>
              <Button
                size="sm"
                variant="outline"
                className="text-destructive"
                onClick={() =>
                  onKillSwitch({
                    service,
                    suspend: true,
                    projects: null,
                    offGatewayProjects: offGateway(service),
                  })
                }
              >
                Stop
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  onKillSwitch({
                    service,
                    suspend: false,
                    projects: null,
                    offGatewayProjects: 0,
                  })
                }
              >
                Run again
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function ServiceCard({
  total,
  deployments,
  offGatewayProjects,
  onKillSwitch,
}: {
  total: AiServiceTotals;
  deployments: NonNullable<AiServicesOverview["registry"]>["llm_deployments"];
  /** Projects that call the service without the gateway: its kill switch misses them. */
  offGatewayProjects: number;
  onKillSwitch: (target: KillSwitchTarget) => void;
}) {
  const unit = unitOf(total.service);
  const used = unit === "usd" ? (total.month_usd ?? 0) : total.month_requests;
  const stopped = total.kill_switch ?? null;

  return (
    <Card className={cn(stopped && "border-destructive/50")}>
      <CardContent className="space-y-3 py-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-muted-foreground text-xs">{SERVICE_LABEL[total.service]}</p>
            <p className="text-primary text-2xl font-bold tabular-nums">
              {formatAmount(unit, used)}
            </p>
            <p className="text-muted-foreground text-xs">
              this month · {total.projects} project{total.projects === 1 ? "" : "s"}
            </p>
          </div>
          {stopped && (
            <Badge variant="destructive" className="text-xs">
              <PauseCircle className="size-3" /> Stopped
            </Badge>
          )}
        </div>

        <p className="text-muted-foreground text-xs">
          {total.at_80_percent} at 80 % · {total.over_budget} over budget · {total.suspended}{" "}
          suspended
        </p>
        {total.month_unpriced && (
          <p className="text-destructive text-xs">
            Some model use has no price yet: those projects count as over budget.
          </p>
        )}

        {deployments.length > 0 && (
          <ul className="space-y-0.5 text-xs">
            {deployments.map((deployment) => (
              <li key={deployment.deployment} className="flex justify-between gap-2">
                <span className="font-mono">{deployment.deployment}</span>
                <span className="text-muted-foreground tabular-nums">
                  {formatAmount("usd", deployment.month_usd)}
                  {deployment.month_unpriced && " + unpriced"} ·{" "}
                  {(deployment.month_input_tokens + deployment.month_output_tokens).toLocaleString(
                    "en-GB",
                  )}{" "}
                  tokens
                </span>
              </li>
            ))}
          </ul>
        )}

        {stopped && (
          <p className="text-destructive text-xs">
            Stopped for every project since {formatDateTime(stopped.suspended_at ?? null)}:{" "}
            <bdi>{stopped.reason}</bdi>
          </p>
        )}
        <Button
          size="sm"
          variant={stopped ? "default" : "outline"}
          className={cn(!stopped && "text-destructive")}
          onClick={() =>
            onKillSwitch({
              service: total.service,
              suspend: !stopped,
              projects: total.projects,
              offGatewayProjects,
            })
          }
        >
          {stopped ? "Run again for every project" : "Stop for every project"}
        </Button>
      </CardContent>
    </Card>
  );
}
